import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { Box, IconButton, Tooltip, Typography, CircularProgress, Menu, MenuItem, Slider } from "@material-ui/core";
import {
  PlayArrow,
  Pause,
  Stop,
  Replay10,
  Forward10,
  VolumeUp,
  VolumeOff,
  VolumeDown,
  VolumeMute,
  Fullscreen,
  FullscreenExit,
  Speed,
  GetApp,
  Videocam,
  VideocamOff,
} from "@material-ui/icons";
import { useAuth } from "../context/ApiKeyContext";
import { getApiBaseUrl } from "../config";
import { useTranslation } from "../hooks/useTranslation";

const API_BASE_URL = getApiBaseUrl();

const AUDIO_EXTENSIONS = new Set([
  "mp3",
  "wav",
  "m4a",
  "aac",
  "ogg",
  "oga",
  "opus",
  "flac",
  "wma",
  "amr",
  "weba",
]);

const VIDEO_EXTENSIONS = new Set([
  "mp4",
  "mov",
  "webm",
  "mkv",
  "avi",
  "wmv",
  "flv",
  "m4v",
  "3gp",
  "ogv",
]);

/* Mapa de extensão → MIME type usado para reconstruir o Blob baixado da API
   com o tipo correto. O endpoint /files/download responde como
   `application/octet-stream` (porque o request envia esse `accept`), e os
   elementos <audio>/<video> rejeitam tocar uma Blob URL cujo type não seja
   um tipo de mídia reconhecido — caso clássico de .m4a/.aac/.opus que
   "não fazem nada" no player. */
const MEDIA_MIME_BY_EXTENSION = {
  /* Áudio */
  mp3: "audio/mpeg",
  wav: "audio/wav",
  m4a: "audio/mp4",
  aac: "audio/aac",
  ogg: "audio/ogg",
  oga: "audio/ogg",
  opus: "audio/ogg",
  flac: "audio/flac",
  wma: "audio/x-ms-wma",
  amr: "audio/amr",
  weba: "audio/webm",
  /* Vídeo */
  mp4: "video/mp4",
  m4v: "video/mp4",
  mov: "video/quicktime",
  webm: "video/webm",
  mkv: "video/x-matroska",
  avi: "video/x-msvideo",
  wmv: "video/x-ms-wmv",
  flv: "video/x-flv",
  "3gp": "video/3gpp",
  ogv: "video/ogg",
};

/* Content-Types que devolvem "binário cru" e que NÃO devem ser preservados
   como tipo do Blob de mídia — sempre serão substituídos pelo MIME inferido
   da extensão do arquivo. */
const GENERIC_BINARY_TYPES = new Set([
  "",
  "application/octet-stream",
  "binary/octet-stream",
  "application/binary",
]);

const inferMediaMime = (filename, fallbackType) => {
  const ext = filename ? String(filename).split(".").pop()?.toLowerCase() : "";
  const fromExt = ext ? MEDIA_MIME_BY_EXTENSION[ext] : null;
  if (fromExt) return fromExt;
  /* Se o backend já devolveu um tipo de mídia específico, preserva-o. */
  if (fallbackType && !GENERIC_BINARY_TYPES.has(fallbackType.toLowerCase())) {
    return fallbackType;
  }
  return "application/octet-stream";
};


const PLAYBACK_RATES = [0.75, 1, 1.5, 2];

const SKIP_SECONDS = 10;

const TIME_UPDATE_INTERVAL_MS = 100;

/* Altura padrão (em px) da área de vídeo. O usuário pode redimensionar
   arrastando a alça inferior; a preferência é persistida no localStorage. */
const VIDEO_DEFAULT_HEIGHT_PX = 360;
const VIDEO_MIN_HEIGHT_PX = 180;
const VIDEO_MAX_HEIGHT_PX = 1200;
const VIDEO_HEIGHT_STORAGE_KEY = "ikaro_media_player_video_height";
/* Preferência "ocultar superfície do vídeo e mostrar só os controles".
   Persistida globalmente: ao abrir QUALQUER vídeo, o usuário recebe o
   modo que escolheu por último. Útil em transcrições em que a tela do
   vídeo é uma distração mas o controle precisa estar à mão. */
const VIDEO_AUDIO_ONLY_STORAGE_KEY = "ikaro_media_player_audio_only";

const inferMediaType = (filename) => {
  if (!filename) return null;
  const ext = String(filename).split(".").pop()?.toLowerCase();
  if (!ext) return null;
  if (AUDIO_EXTENSIONS.has(ext)) return "audio";
  if (VIDEO_EXTENSIONS.has(ext)) return "video";
  return null;
};

export const isAudioFile = (filename) => inferMediaType(filename) === "audio";
export const isVideoFile = (filename) => inferMediaType(filename) === "video";
export const isMediaFile = (filename) => inferMediaType(filename) != null;

const formatTime = (seconds) => {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const total = Math.floor(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n) => String(n).padStart(2, "0");
  if (h > 0) return `${h}:${pad(m)}:${pad(s)}`;
  return `${m}:${pad(s)}`;
};

/**
 * MediaPlayer
 *
 * Reprodutor de áudio/vídeo com:
 *   - Barra de progresso navegável
 *   - Play/pause/stop, retroceder/avançar 10s
 *   - Mute, controle de volume
 *   - Velocidade (75%/100%/150%/200%)
 *   - Fullscreen (vídeo)
 *   - Download do arquivo
 *
 * Aceita controle imperativo via `ref` (play, pause, stop, seek, setRate,
 * getCurrentTime, getDuration, isPlaying), e dispara `onTimeUpdate` a cada
 * 100ms enquanto está reproduzindo (mais frequente que o evento nativo).
 *
 * Fontes aceitas:
 *   - `src`: URL pública direta (prioridade máxima)
 *   - `file` ({ token, filename }) ou `token` + `filename` (baixa via
 *     `/files/download` autenticado e usa um Blob URL)
 */
const MediaPlayer = forwardRef(function MediaPlayer(
  {
    src,
    file,
    token: fileToken,
    filename,
    serviceName = "DOCUMENT_CREATION",
    mediaType,
    autoPlay = false,
    showDownload = true,
    showFullscreen = true,
    onTimeUpdate,
    onPlay,
    onPause,
    onEnded,
    onLoadedMetadata,
    onError,
    className,
    style,
  },
  ref,
) {
  const { token: authToken } = useAuth();
  const { t } = useTranslation("common");

  const containerRef = useRef(null);
  const mediaRef = useRef(null);
  const onTimeUpdateRef = useRef(onTimeUpdate);

  useEffect(() => {
    onTimeUpdateRef.current = onTimeUpdate;
  }, [onTimeUpdate]);

  const resolvedToken = file?.token ?? fileToken ?? null;
  const resolvedFilename = file?.filename ?? filename ?? "media";
  const resolvedMediaType = useMemo(
    () => mediaType || inferMediaType(resolvedFilename) || "audio",
    [mediaType, resolvedFilename],
  );

  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [muted, setMuted] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [rateAnchorEl, setRateAnchorEl] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  /* Altura da área de vídeo em pixels. Inicializada com a preferência
     salva (se houver) ou com o default. Valores fora do intervalo
     permitido são ignorados. */
  const [videoHeight, setVideoHeight] = useState(() => {
    if (typeof window === "undefined") return VIDEO_DEFAULT_HEIGHT_PX;
    try {
      const saved = window.localStorage.getItem(VIDEO_HEIGHT_STORAGE_KEY);
      const parsed = saved != null ? parseInt(saved, 10) : NaN;
      if (
        Number.isFinite(parsed) &&
        parsed >= VIDEO_MIN_HEIGHT_PX &&
        parsed <= VIDEO_MAX_HEIGHT_PX
      ) {
        return parsed;
      }
    } catch (_) {
      /* localStorage indisponível (modo privado, SSR, etc.) — usa default */
    }
    return VIDEO_DEFAULT_HEIGHT_PX;
  });
  const [isResizing, setIsResizing] = useState(false);

  /* Modo "só áudio" para mídia de vídeo: mantém o <video> montado (para
     não interromper reprodução nem decodificação), apenas oculta a
     superfície visível e a alça de resize. Estado e ícone só aparecem
     quando a mídia é de fato vídeo. */
  const [audioOnly, setAudioOnly] = useState(() => {
    if (typeof window === "undefined") return false;
    try {
      return window.localStorage.getItem(VIDEO_AUDIO_ONLY_STORAGE_KEY) === "1";
    } catch (_) {
      return false;
    }
  });
  const toggleAudioOnly = useCallback(() => {
    setAudioOnly((prev) => {
      const next = !prev;
      try {
        if (typeof window !== "undefined") {
          window.localStorage.setItem(
            VIDEO_AUDIO_ONLY_STORAGE_KEY,
            next ? "1" : "0",
          );
        }
      } catch (_) {
        /* localStorage indisponível — preferência só vive nessa sessão */
      }
      return next;
    });
  }, []);

  /* Carrega o arquivo via API autenticada quando uma URL direta não foi
     fornecida. Usamos Blob URL porque os endpoints exigem o JWT no header
     Authorization e tags <audio>/<video> não permitem custom headers. */
  useEffect(() => {
    if (src) {
      setBlobUrl(null);
      return undefined;
    }
    if (!resolvedToken || !authToken) {
      return undefined;
    }

    let cancelled = false;
    let createdUrl = null;

    setError(null);
    setLoading(true);

    const url = `${API_BASE_URL}/files/download?token=${encodeURIComponent(
      resolvedToken,
    )}&Servicename=${encodeURIComponent(serviceName)}`;

    fetch(url, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${authToken}`,
        accept: "application/octet-stream",
      },
    })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`${response.status} ${response.statusText}`);
        }
        const rawBlob = await response.blob();
        /* Reconstrói o Blob com o MIME type correto (inferido pela
           extensão do arquivo). Sem isso, formatos como .m4a/.aac/.opus
           ficam com type=application/octet-stream e o <audio>/<video>
           se recusa a tocar a Blob URL. */
        const mediaMime = inferMediaMime(resolvedFilename, rawBlob.type);
        if (mediaMime && mediaMime !== rawBlob.type) {
          return rawBlob.slice(0, rawBlob.size, mediaMime);
        }
        return rawBlob;
      })
      .then((blob) => {
        if (cancelled) return;
        createdUrl = URL.createObjectURL(blob);
        setBlobUrl(createdUrl);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("MediaPlayer: erro ao carregar mídia", err);
        setError(err?.message || String(err));
        setLoading(false);
      });

    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [src, resolvedToken, resolvedFilename, authToken, serviceName]);

  const playbackSrc = src || blobUrl || null;

  /* Garante que mudanças de velocidade (e mute) sejam aplicadas ao elemento
     mesmo quando ele é remontado (ex.: troca de fonte). */
  useEffect(() => {
    if (mediaRef.current) {
      mediaRef.current.playbackRate = playbackRate;
    }
  }, [playbackRate, playbackSrc]);

  useEffect(() => {
    if (mediaRef.current) {
      mediaRef.current.volume = volume;
      mediaRef.current.muted = muted;
    }
  }, [volume, muted, playbackSrc]);

  /* Polling de tempo para callbacks de alta frequência. O evento nativo
     `timeupdate` dispara apenas a cada ~250ms, insuficiente para sincronizar
     com transcrições. */
  useEffect(() => {
    if (!isPlaying) return undefined;
    const interval = setInterval(() => {
      const el = mediaRef.current;
      if (!el) return;
      setCurrentTime(el.currentTime);
      const cb = onTimeUpdateRef.current;
      if (cb) {
        try {
          cb(el.currentTime);
        } catch (err) {
          console.error("MediaPlayer: onTimeUpdate handler falhou", err);
        }
      }
    }, TIME_UPDATE_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [isPlaying]);

  /* Acompanha mudança de fullscreen via API do navegador para sincronizar o
     ícone do botão. */
  useEffect(() => {
    const handler = () => {
      const el = document.fullscreenElement;
      setIsFullscreen(Boolean(el && containerRef.current?.contains(el)) || el === containerRef.current);
    };
    document.addEventListener("fullscreenchange", handler);
    return () => document.removeEventListener("fullscreenchange", handler);
  }, []);

  const handleLoadedMetadata = useCallback(
    (e) => {
      const d = e.currentTarget.duration;
      if (Number.isFinite(d)) setDuration(d);
      if (onLoadedMetadata) onLoadedMetadata(e);
    },
    [onLoadedMetadata],
  );

  const handleTimeUpdate = useCallback((e) => {
    setCurrentTime(e.currentTarget.currentTime);
  }, []);

  const handlePlay = useCallback(
    (e) => {
      setIsPlaying(true);
      if (onPlay) onPlay(e);
    },
    [onPlay],
  );

  const handlePause = useCallback(
    (e) => {
      setIsPlaying(false);
      if (onPause) onPause(e);
    },
    [onPause],
  );

  const handleEnded = useCallback(
    (e) => {
      setIsPlaying(false);
      if (onEnded) onEnded(e);
    },
    [onEnded],
  );

  const handleVolumeChangeNative = useCallback((e) => {
    const el = e.currentTarget;
    setVolume(el.volume);
    setMuted(el.muted);
  }, []);

  const handleMediaError = useCallback(
    (e) => {
      const err = e?.currentTarget?.error;
      const msg = err ? `Media error (code ${err.code})` : "Erro de reprodução";
      setError(msg);
      if (onError) onError(e);
    },
    [onError],
  );

  const togglePlay = useCallback(() => {
    const el = mediaRef.current;
    if (!el) return;
    if (el.paused) {
      const p = el.play();
      if (p && typeof p.catch === "function") {
        p.catch((err) => {
          console.error("MediaPlayer: falha ao iniciar reprodução", err);
        });
      }
    } else {
      el.pause();
    }
  }, []);

  const stop = useCallback(() => {
    const el = mediaRef.current;
    if (!el) return;
    el.pause();
    el.currentTime = 0;
    setCurrentTime(0);
  }, []);

  const skip = useCallback((delta) => {
    const el = mediaRef.current;
    if (!el) return;
    const next = Math.max(0, Math.min((el.duration || 0), el.currentTime + delta));
    el.currentTime = next;
    setCurrentTime(next);
  }, []);

  const handleSeek = useCallback((_e, value) => {
    const el = mediaRef.current;
    if (!el) return;
    const v = Array.isArray(value) ? value[0] : value;
    el.currentTime = v;
    setCurrentTime(v);
  }, []);

  const handleVolumeSlider = useCallback((_e, value) => {
    const v = Array.isArray(value) ? value[0] : value;
    setVolume(v);
    setMuted(v === 0);
    if (mediaRef.current) {
      mediaRef.current.volume = v;
      mediaRef.current.muted = v === 0;
    }
  }, []);

  const toggleMute = useCallback(() => {
    setMuted((prev) => {
      const next = !prev;
      if (mediaRef.current) mediaRef.current.muted = next;
      return next;
    });
  }, []);

  const handleRateMenuOpen = useCallback((e) => {
    setRateAnchorEl(e.currentTarget);
  }, []);
  const handleRateMenuClose = useCallback(() => {
    setRateAnchorEl(null);
  }, []);
  const handleSelectRate = useCallback((rate) => {
    setPlaybackRate(rate);
    if (mediaRef.current) mediaRef.current.playbackRate = rate;
    setRateAnchorEl(null);
  }, []);

  const toggleFullscreen = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    if (!document.fullscreenElement) {
      const req =
        container.requestFullscreen ||
        container.webkitRequestFullscreen ||
        container.msRequestFullscreen;
      if (req) {
        try {
          req.call(container);
        } catch (err) {
          console.error("MediaPlayer: falha ao entrar em fullscreen", err);
        }
      }
    } else {
      const exit =
        document.exitFullscreen ||
        document.webkitExitFullscreen ||
        document.msExitFullscreen;
      if (exit) {
        try {
          exit.call(document);
        } catch (err) {
          console.error("MediaPlayer: falha ao sair de fullscreen", err);
        }
      }
    }
  }, []);

  const handleDownload = useCallback(async () => {
    if (src) {
      const link = document.createElement("a");
      link.href = src;
      link.download = resolvedFilename || "media";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return;
    }
    if (!resolvedToken || !authToken) return;
    try {
      const url = `${API_BASE_URL}/files/download?token=${encodeURIComponent(
        resolvedToken,
      )}&Servicename=${encodeURIComponent(serviceName)}`;
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${authToken}`,
          accept: "application/octet-stream",
        },
      });
      if (!response.ok) {
        throw new Error(`${response.status} ${response.statusText}`);
      }
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = resolvedFilename || "media";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      console.error("MediaPlayer: erro ao baixar arquivo", err);
      setError(err?.message || String(err));
    }
  }, [src, resolvedToken, resolvedFilename, authToken, serviceName]);

  /* ─────────────── Redimensionamento da área de vídeo ───────────────

     Implementação: ouvimos `pointermove`/`pointerup` no `document` (e não
     na alça em si) para que o arrasto continue mesmo quando o cursor sai
     da alça — comportamento padrão de "splitters" em IDEs/Figma. A altura
     final é persistida no localStorage para que a preferência sobreviva
     entre sessões/recargas. */

  const resizeStartRef = useRef({ y: 0, height: 0 });
  /* Mantemos a altura atual em ref também, para que o cleanup de unmount
     consiga ler o último valor sem reagendar handlers. */
  const videoHeightRef = useRef(videoHeight);
  useEffect(() => {
    videoHeightRef.current = videoHeight;
  }, [videoHeight]);

  const handleResizeMove = useCallback((e) => {
    const dy = e.clientY - resizeStartRef.current.y;
    const proposed = resizeStartRef.current.height + dy;
    const next = Math.max(
      VIDEO_MIN_HEIGHT_PX,
      Math.min(VIDEO_MAX_HEIGHT_PX, Math.round(proposed)),
    );
    setVideoHeight(next);
  }, []);

  const handleResizeEnd = useCallback(() => {
    setIsResizing(false);
    document.removeEventListener("pointermove", handleResizeMove);
    document.removeEventListener("pointerup", handleResizeEnd);
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
    try {
      window.localStorage.setItem(
        VIDEO_HEIGHT_STORAGE_KEY,
        String(videoHeightRef.current),
      );
    } catch (_) {
      /* ignora se localStorage estiver indisponível */
    }
  }, [handleResizeMove]);

  const handleResizeStart = useCallback(
    (e) => {
      /* Apenas botão primário do mouse / toque (pointer). */
      if (e.button != null && e.button !== 0) return;
      e.preventDefault();
      resizeStartRef.current = {
        y: e.clientY,
        height: videoHeightRef.current,
      };
      setIsResizing(true);
      document.addEventListener("pointermove", handleResizeMove);
      document.addEventListener("pointerup", handleResizeEnd);
      /* `row-resize` é o cursor canônico para divisores entre linhas
         (visualmente mostra uma seta dupla vertical separada por uma
         barra horizontal — mais auto-explicativo que `ns-resize`). */
      document.body.style.cursor = "row-resize";
      /* Evita seleção acidental de texto enquanto o usuário arrasta. */
      document.body.style.userSelect = "none";
    },
    [handleResizeMove, handleResizeEnd],
  );

  const handleResizeReset = useCallback(() => {
    setVideoHeight(VIDEO_DEFAULT_HEIGHT_PX);
    try {
      window.localStorage.setItem(
        VIDEO_HEIGHT_STORAGE_KEY,
        String(VIDEO_DEFAULT_HEIGHT_PX),
      );
    } catch (_) {
      /* ignora se localStorage estiver indisponível */
    }
  }, []);

  /* Garante que, se o componente for desmontado durante um arrasto, os
     listeners globais e o estilo do `body` sejam revertidos. */
  useEffect(
    () => () => {
      document.removeEventListener("pointermove", handleResizeMove);
      document.removeEventListener("pointerup", handleResizeEnd);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    },
    [handleResizeMove, handleResizeEnd],
  );

  /* API imperativa exposta para componentes externos sincronizarem
     reprodução/posição. */
  useImperativeHandle(
    ref,
    () => ({
      play: () => {
        const el = mediaRef.current;
        if (!el) return undefined;
        return el.play();
      },
      pause: () => {
        mediaRef.current?.pause();
      },
      stop,
      seek: (seconds) => {
        const el = mediaRef.current;
        if (!el) return;
        const safe = Math.max(0, Number(seconds) || 0);
        el.currentTime = safe;
        setCurrentTime(safe);
      },
      setRate: (rate) => {
        const safe = Number(rate) || 1;
        setPlaybackRate(safe);
        if (mediaRef.current) mediaRef.current.playbackRate = safe;
      },
      setVolume: (v) => {
        const safe = Math.max(0, Math.min(1, Number(v)));
        setVolume(safe);
        if (mediaRef.current) mediaRef.current.volume = safe;
      },
      setMuted: (m) => {
        const next = Boolean(m);
        setMuted(next);
        if (mediaRef.current) mediaRef.current.muted = next;
      },
      getCurrentTime: () => mediaRef.current?.currentTime ?? 0,
      getDuration: () => mediaRef.current?.duration ?? 0,
      isPlaying: () => (mediaRef.current ? !mediaRef.current.paused : false),
      getElement: () => mediaRef.current,
    }),
    [stop],
  );

  const VolumeIcon = muted || volume === 0
    ? VolumeOff
    : volume < 0.33
      ? VolumeMute
      : volume < 0.66
        ? VolumeDown
        : VolumeUp;

  const isVideo = resolvedMediaType === "video";

  const containerClass = [
    "ikaro-media-player",
    isVideo ? "ikaro-media-player--video" : "ikaro-media-player--audio",
    className || "",
  ]
    .filter(Boolean)
    .join(" ");

  const renderMediaElement = () => {
    if (loading) {
      return (
        <Box className="ikaro-media-player__loader">
          <CircularProgress size={32} />
          <Typography variant="caption" className="ikaro-media-player__loader-text">
            {t("loading")}
          </Typography>
        </Box>
      );
    }
    if (error) {
      return (
        <Box className="ikaro-media-player__error">
          <Typography variant="body2">
            {t("mediaPlayer.loadError")}
          </Typography>
          <Typography variant="caption">{error}</Typography>
        </Box>
      );
    }
    if (!playbackSrc) {
      return (
        <Box className="ikaro-media-player__error">
          <Typography variant="body2">{t("mediaPlayer.noSource")}</Typography>
        </Box>
      );
    }

    /* `preload="auto"` quando a fonte é uma Blob URL local: os bytes já
       estão no navegador e não há custo de banda extra. Para URLs
       externas (`src` direto), mantemos `metadata` para evitar
       pré-carregar arquivos grandes da rede sem necessidade. */
    const isBlobSource =
      typeof playbackSrc === "string" && playbackSrc.startsWith("blob:");
    const commonProps = {
      ref: mediaRef,
      src: playbackSrc,
      preload: isBlobSource ? "auto" : "metadata",
      autoPlay: autoPlay,
      onLoadedMetadata: handleLoadedMetadata,
      onTimeUpdate: handleTimeUpdate,
      onPlay: handlePlay,
      onPause: handlePause,
      onEnded: handleEnded,
      onVolumeChange: handleVolumeChangeNative,
      onError: handleMediaError,
    };

    if (isVideo) {
      return (
        <video
          {...commonProps}
          className="ikaro-media-player__video"
          playsInline
        />
      );
    }
    return <audio {...commonProps} className="ikaro-media-player__audio" />;
  };

  /* Quando o usuário ativa "só áudio" em mídia de vídeo, tratamos o player
     visualmente como áudio: sem área de mídia, sem alça, sem fullscreen. */
  const effectiveAudioOnly = isVideo && audioOnly && !isFullscreen;
  /* Altura aplicada na área de mídia: só faz sentido para vídeo e quando
     NÃO estamos em fullscreen (este último cobre a tela toda) nem em
     modo "só áudio". */
  const showVideoResize = isVideo && !isFullscreen && !effectiveAudioOnly;
  const mediaAreaStyle = showVideoResize
    ? { height: `${videoHeight}px` }
    : undefined;
  /* O elemento <video> precisa continuar montado para preservar o estado
     de reprodução; apenas escondemos sua área visualmente. */
  const mediaAreaHidden = effectiveAudioOnly;

  return (
    <Box
      ref={containerRef}
      className={containerClass}
      style={style}
      data-fullscreen={isFullscreen ? "true" : "false"}
      data-audio-only={effectiveAudioOnly ? "true" : "false"}
    >
      <Box
        className="ikaro-media-player__media-area"
        style={mediaAreaHidden ? { display: "none" } : mediaAreaStyle}
        onClick={isVideo && !mediaAreaHidden ? togglePlay : undefined}
        aria-hidden={mediaAreaHidden ? "true" : undefined}
      >
        {renderMediaElement()}
      </Box>

      {showVideoResize && (
        <Box
          className={[
            "ikaro-media-player__resize-handle",
            isResizing ? "is-resizing" : "",
          ]
            .filter(Boolean)
            .join(" ")}
          onPointerDown={handleResizeStart}
          onDoubleClick={handleResizeReset}
          role="separator"
          aria-orientation="horizontal"
          aria-label={t("mediaPlayer.resizeHandle")}
          title={t("mediaPlayer.resizeHandleHint")}
        >
          <Box className="ikaro-media-player__resize-handle-grip" />
        </Box>
      )}

      <Box className="ikaro-media-player__controls">
        <Box className="ikaro-media-player__progress-row">
          <Typography variant="caption" className="ikaro-media-player__time">
            {formatTime(currentTime)}
          </Typography>
          <Slider
            className="ikaro-media-player__progress"
            value={Math.min(currentTime, duration || 0)}
            min={0}
            max={duration || 0}
            step={0.1}
            onChange={handleSeek}
            disabled={!playbackSrc || !duration}
            aria-label={t("mediaPlayer.seek")}
          />
          <Typography variant="caption" className="ikaro-media-player__time">
            {formatTime(duration)}
          </Typography>
        </Box>

        <Box className="ikaro-media-player__buttons-row">
          <Box className="ikaro-media-player__btn-group">
            <Tooltip title={isPlaying ? t("mediaPlayer.pause") : t("mediaPlayer.play")}>
              <span>
                <IconButton
                  size="small"
                  className="ikaro-media-player__btn ikaro-media-player__btn--primary"
                  onClick={togglePlay}
                  disabled={!playbackSrc}
                >
                  {isPlaying ? <Pause /> : <PlayArrow />}
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip title={t("mediaPlayer.stop")}>
              <span>
                <IconButton
                  size="small"
                  className="ikaro-media-player__btn"
                  onClick={stop}
                  disabled={!playbackSrc}
                >
                  <Stop />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip title={t("mediaPlayer.skipBack")}>
              <span>
                <IconButton
                  size="small"
                  className="ikaro-media-player__btn"
                  onClick={() => skip(-SKIP_SECONDS)}
                  disabled={!playbackSrc}
                >
                  <Replay10 />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip title={t("mediaPlayer.skipForward")}>
              <span>
                <IconButton
                  size="small"
                  className="ikaro-media-player__btn"
                  onClick={() => skip(SKIP_SECONDS)}
                  disabled={!playbackSrc}
                >
                  <Forward10 />
                </IconButton>
              </span>
            </Tooltip>
          </Box>

          <Box className="ikaro-media-player__btn-group ikaro-media-player__volume-group">
            <Tooltip title={muted ? t("mediaPlayer.unmute") : t("mediaPlayer.mute")}>
              <span>
                <IconButton
                  size="small"
                  className="ikaro-media-player__btn"
                  onClick={toggleMute}
                  disabled={!playbackSrc}
                >
                  <VolumeIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Slider
              className="ikaro-media-player__volume-slider"
              value={muted ? 0 : volume}
              min={0}
              max={1}
              step={0.01}
              onChange={handleVolumeSlider}
              disabled={!playbackSrc}
              aria-label={t("mediaPlayer.volume")}
            />
          </Box>

          <Box className="ikaro-media-player__btn-group">
            <Tooltip title={t("mediaPlayer.playbackRate")}>
              <span>
                <IconButton
                  size="small"
                  className="ikaro-media-player__btn ikaro-media-player__rate-btn"
                  onClick={handleRateMenuOpen}
                  disabled={!playbackSrc}
                >
                  <Speed />
                  <Typography
                    component="span"
                    variant="caption"
                    className="ikaro-media-player__rate-label"
                  >
                    {`${Math.round(playbackRate * 100)}%`}
                  </Typography>
                </IconButton>
              </span>
            </Tooltip>
            <Menu
              anchorEl={rateAnchorEl}
              open={Boolean(rateAnchorEl)}
              onClose={handleRateMenuClose}
            >
              {PLAYBACK_RATES.map((rate) => (
                <MenuItem
                  key={rate}
                  selected={rate === playbackRate}
                  onClick={() => handleSelectRate(rate)}
                >
                  {`${Math.round(rate * 100)}%`}
                </MenuItem>
              ))}
            </Menu>

            {isVideo && (
              <Tooltip
                title={
                  audioOnly
                    ? t("mediaPlayer.showVideo")
                    : t("mediaPlayer.hideVideo")
                }
              >
                <span>
                  <IconButton
                    size="small"
                    className="ikaro-media-player__btn"
                    onClick={toggleAudioOnly}
                    disabled={!playbackSrc}
                    aria-pressed={audioOnly ? "true" : "false"}
                  >
                    {audioOnly ? <Videocam /> : <VideocamOff />}
                  </IconButton>
                </span>
              </Tooltip>
            )}

            {isVideo && showFullscreen && !effectiveAudioOnly && (
              <Tooltip
                title={
                  isFullscreen
                    ? t("mediaPlayer.exitFullscreen")
                    : t("mediaPlayer.fullscreen")
                }
              >
                <span>
                  <IconButton
                    size="small"
                    className="ikaro-media-player__btn"
                    onClick={toggleFullscreen}
                    disabled={!playbackSrc}
                  >
                    {isFullscreen ? <FullscreenExit /> : <Fullscreen />}
                  </IconButton>
                </span>
              </Tooltip>
            )}

            {showDownload && (resolvedToken || src) && (
              <Tooltip title={t("mediaPlayer.download")}>
                <span>
                  <IconButton
                    size="small"
                    className="ikaro-media-player__btn"
                    onClick={handleDownload}
                  >
                    <GetApp />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
});

export default MediaPlayer;
